<template>
  <q-page>
    <div class="panel row justify-center">
      <div class="col-12 col-sm-9 col-md-6 col-lg-4 col-xl-3">
        <!-- ===== HEADER ================================================== -->

        <h2 class="text-center">
          <span class="druk">JUIT</span>
        </h2>

        <!-- ===== FORM ==================================================== -->

        <z-form
          ref="_form"
          class="column q-gutter-sm"
          bottom-slots
          editable
          @submit="login"
        >
          <!-- Email -->
          <z-string
            ref="emailRef"
            v-model="email"
            :rules="[ validators.email() ]"
            :label="$t({ en: 'Email', de: 'Email' })"
            required
          />

          <!-- Password -->
          <z-password
            ref="passwordRef"
            v-model="password"
            :label="$t({ en: 'Password', de: 'Passwort' })"
            required
            @action-click="showPassword = !showPassword"
          />

          <!-- Login Button -->
          <z-btn
            :label="$t('login')"
            :color="(disable || loading) ? 'grey' : 'primary'"
            :loading="loading"
            :disable="disable"
            type="submit"
          />
        </z-form>
      </div>
    </div>
  </q-page>
</template>

<script setup lang="ts">
import { useTranslator } from '@juit/vue-i18n'
import { createLoader, useValidators } from '@juit/vue-z'
import { useQuasar } from 'quasar'
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'

import { client } from '@/init/client'

import type { ZPassword, ZString } from '@juit/vue-z'

const validators = useValidators()
const { t } = useTranslator()

const { notify } = useQuasar()
const router = useRouter()

// form references
const emailRef = ref<ZString>()
const passwordRef = ref<ZPassword>()

/** Email address */
const email = ref('')
/** Password */
const password = ref('')

/** Are we logging in? */
const { loading, load } = createLoader()
/** Should we show the password? */
const showPassword = ref(false)

/** Is email or password empty? */
const isEmpty = computed(() => ! (email.value && password.value))
/** Is email or password in error? */
const hasError = computed(() => emailRef.value?.hasError || passwordRef.value?.hasError)
/** Should we disable the login button? */
const disable = computed(() => isEmpty.value || hasError.value )

/** Perform the login */
function login(): void {
  if (disable.value || loading.value) return

  load(async () => {
    try {
      const user = await client.oauth.login({ email: email.value, password: password.value })
      if (user) {
        router.push({ name: 'Home' })
        return
      }
      // Wait... Why do we have no user here?
      notify({
        type: 'negative',
        message: t({
          en: 'Unknown error logging in',
          de: 'Unbekannter Fehler beim Anmelden',
        }),
      })
    } catch (error: any) {
      // a 401 error means that username/password was wrong
      if (error?.status === 401) {
        notify({
          type: 'negative',
          message: t({
            en: 'Invalid email/password combination',
            de: 'Ungültige E-Mail/Passwort Kombination',
          }),
        })
      } else {
        // any error gets reported back the default window handlers
        reportError(error)
        notify({
          type: 'negative',
          message: t({
            en: 'Unknown error occurred logging in',
            de: 'Beim Anmelden ist ein unbekannter Fehler aufgetreten',
          }),
        })
      }
    }
  })
}
</script>

<style lang="css" scoped>
.panel {
  position: absolute;
  transform: translateY(-50%);
  top: 50%;
  left: 0;
  right: 0;
}
</style>
